import React, {forwardRef, ReactNode} from 'react';
import classnames from 'classnames';
import {format, parse} from 'date-fns';
import DatePicker, {ReactDatePickerProps} from 'react-datepicker';
import en from 'date-fns/locale/en-GB';
import he from 'date-fns/locale/he';
import {useSelector} from 'react-redux';
import ButtonAction from '../ButtonAction';
import {RootState} from '../../../../store/configureStore';
import icon from '../../../assets/icons/cancel-circle.svg';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './InputDate.module.scss';

export interface InputDateRangeProps extends ReactDatePickerProps<any, false> {
  id?: string
  onReset?: () => void
  label?: ReactNode | string
  error?: string
}

export interface CustomInputProps {
  currentLocale: string,
  value?: string,
  placeholder?: string,
  className?: string,
  onClick?: () => void,
  disabled?: boolean,
  onReset?: () => void,
}

export type Ref = HTMLButtonElement;


const CustomInput = forwardRef<Ref, CustomInputProps>(({
                                                         value,
                                                         placeholder,
                                                         className,
                                                         onClick,
                                                         disabled,
                                                         currentLocale,
                                                         onReset
                                                       }, ref) => {
  const getInputValue = () => {
    const initialDate = Boolean(value) && typeof value === 'string'
      ? parse(value, 'dd/MM/yyyy', new Date())
      : new Date();

    const date = Boolean(initialDate)
      ? format(
        initialDate,
        'dd MMM yy',
        {locale: (currentLocale === 'he') ? he : en}
      )
      : '';

    if (value) {
      return <span>{date}</span>
    } else {
      return placeholder;
    }
  }

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onReset) {
      onReset();
    }
  };

  return (
    <div
      className={classnames(
        {[styles.disabled]: disabled},
        className
      )}
      onClick={onClick}
      {...ref}
    >
          <span className={classnames({[styles.placeholder]: Boolean(!value)})}>
            {getInputValue()}
          </span>
      {(onReset) && (
        <ButtonAction
          variant={'icon'}
          icon={icon}
          onClick={handleReset}
          customClassnames={{container: styles.btnReset}}
        />
      )}
    </div>);
});

const InputDate = ({startDate, onChange, onReset, placeholderText, label, error, minDate, maxDate}: InputDateRangeProps) => {
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);

  return (
    <div className={styles.container}>
      {label && (
        <div className={styles.label}>
          {label}
        </div>
      )}
      <DatePicker
        selected={startDate}
        onChange={onChange}
        dateFormat="dd/MM/yyyy"
        locale={(currentLocale === 'he') ? he : en}
        placeholderText={placeholderText}
        wrapperClassName={styles.wrapperDatepicker}
        className={styles.datepicker}
        customInput={(
          <CustomInput
            currentLocale={currentLocale}
            onReset={onReset}
          />
        )}
        minDate={minDate}
        maxDate={maxDate}
      />
      <div className={styles.errorContainer}>
        {error && (
          <div className={styles.error}>{error}</div>
        )}
      </div>
    </div>
  );
};

export default InputDate;
